/**
 * FirstRunConnectorGrid — Supported connector cards for the first-run experience
 *
 * Shows the 3 supported connectors: Excel, CSV, Salesforce.
 */

import React from 'react';
import {
  Text,
  Badge,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { ConnectorIcon } from '../../utils/connectorIcons';
import { isProduction } from '../../utils/environment';

// ─── Connector definitions ───────────────────────────────────────────────────

interface ConnectorDef {
  id: string;
  name: string;
  description: string;
  timingHint: string;
  badge?: string;
  /** Whether to mute/de-emphasize the card */
  muted?: boolean;
  /** Only show in dev/test environments */
  devOnly?: boolean;
}

const SUPPORTED_CONNECTORS: ConnectorDef[] = [
  {
    id: 'excel',
    name: 'Excel',
    description: 'Upload a spreadsheet and get a dashboard in minutes.',
    timingHint: 'Usually ready in ~2 minutes',
  },
  {
    id: 'csv',
    name: 'CSV',
    description: 'Upload a CSV file and let AI profile and model it.',
    timingHint: 'Usually ready in ~2 minutes',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Connect CRM data, discover entities, relationships, pipeline metrics, and dashboards.',
    timingHint: 'Large orgs can take 30–60 minutes',
  },
  {
    id: 'zoho',
    name: 'Zoho CRM',
    description: 'Connect Zoho CRM modules and build dashboards from sales and activity data.',
    timingHint: 'Large organizations can take 30–60 minutes',
  },
];

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '1060px',
    margin: '0 auto',
    width: '100%',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  grid: {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: 'repeat(3, minmax(240px, 1fr))',
    justifyContent: 'center',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    ...shorthands.padding('16px', '18px'),
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transitionProperty: 'background-color, box-shadow',
    transitionDuration: '150ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      boxShadow: tokens.shadow2,
    },
  },
  cardMuted: {
    opacity: 0.7,
    ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke2),
    ':hover': {
      opacity: 1,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  cardName: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  cardDescription: {
    fontSize: '12px',
    lineHeight: '17px',
    color: tokens.colorNeutralForeground2,
  },
  cardTiming: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    marginTop: 'auto',
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

const FirstRunConnectorGrid: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const isProd = isProduction();

  const visibleConnectors = SUPPORTED_CONNECTORS.filter(c => {
    if (c.devOnly && isProd) return false;
    return true;
  });

  const handleSelect = (connectorId: string) => {
    navigate(`/data-sources/new/connect?connector=${connectorId}`);
  };

  return (
    <div className={styles.root}>
      <Text className={styles.sectionTitle}>Supported data sources</Text>
      <div className={styles.grid}>
        {visibleConnectors.map(connector => (
          <div
            key={connector.id}
            className={mergeClasses(styles.card, connector.muted && styles.cardMuted)}
            role="button"
            tabIndex={0}
            onClick={() => handleSelect(connector.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect(connector.id);
              }
            }}
          >
            <div className={styles.cardHeader}>
              <ConnectorIcon connectorId={connector.id} name={connector.name} size={22} />
              <Text className={styles.cardName}>{connector.name}</Text>
              {connector.badge && (
                <Badge
                  appearance="outline"
                  color="informative"
                  size="small"
                >
                  {connector.badge}
                </Badge>
              )}
            </div>
            <Text className={styles.cardDescription}>{connector.description}</Text>
            <Text className={styles.cardTiming}>{connector.timingHint}</Text>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FirstRunConnectorGrid;